/**
 * [T157 / Calendar-Insight follow-up] CalendarMatrix — CSS-grid heatmap calendar primitive
 * with orientation projection + active-cell controlled popup state machine.
 *
 * Renders a partial-year spending calendar in two layout projections:
 *   • horizontal (default): ISO weeks as columns, weekdays as rows.
 *   • vertical (mobile <768): ISO weeks as rows, weekdays as columns.
 *
 * Both projections share one normalized data model (`CalendarMatrixDay`) and one
 * active-cell state (`activeDate`). The popup uses Radix Popover in controlled mode
 * with collision-aware placement and a cancellable close-grace so a pointer can
 * travel from a trigger cell into its popover content without the popover collapsing
 * underneath.
 *
 * Vertical mode adds (M3 follow-up):
 *   • a left-edge sticky month-name gutter, one centered abbreviation per ISO week row.
 *   • a day-of-month number inside every cell, with theme-aware contrast.
 *   • a border-l-2 border-l-ring marker on the first day of each new month.
 *   • max-h-[520px] overflow-y-auto wrapper applied only when monthCount > 2,
 *     so two months fit without internal scroll and longer ranges scroll inside the
 *     calendar container instead of overflowing the document.
 *
 * Interaction state machine (unified across mouse, touch, and keyboard):
 *   mouse hover A → A: open/transfer popup immediately.
 *   mouse moves A → B: atomically replace A with B.
 *   mouse leaves trigger: short cancellable close grace (~150ms).
 *   pointer enters popup: cancel close; popup remains readable.
 *   pointer leaves popup (and trigger): close; no stale bottom popup.
 *   touch tap A when closed: open A.
 *   touch tap A when A is open: close A.
 *   touch tap B while A is open: close A and open B.
 *   keyboard focus A: open/transfer popup (modality-agnostic).
 *   Escape: close popup and preserve useful focus.
 *   Enter / Space on focused cell: same as touch tap.
 *
 * The grid is never converted to a table and the mobile projection never falls
 * back to a chronological list. The existing `aria-label` on each cell remains
 * the semantic source-of-truth; the day-of-month number is purely visual.
 */
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface CalendarMatrixDay {
  /** ISO `YYYY-MM-DD`. */
  date: string;
  total: number;
  count: number;
  /** 0..1 — pre-computed or derived as total / max(totals). */
  intensity: number;
}

export type CalendarOrientation = 'horizontal' | 'vertical';

export interface CalendarMatrixProps {
  days: CalendarMatrixDay[];
  /** Cell size in px. Default 24 (horizontal) / 44 (vertical, 44px touch target). */
  cellSize?: number;
  /** Gap in px between cells. Default 2. */
  gap?: number;
  /** Render Mon–Sun day-of-week labels. Default true. */
  showDayLabels?: boolean;
  /** Layout projection. Default 'horizontal'. */
  orientation?: CalendarOrientation;
  /** Controlled active date key (ISO YYYY-MM-DD). When null, no popup is open. */
  activeDate: string | null;
  /** Called when the active date should change (toggle/transfer/close). */
  onActiveDateChange: (key: string | null) => void;
  /** Open the transaction list filtered to this day and return focus afterward. */
  onViewTransactions?: (date: string, returnFocus: () => void) => void;
  /** Close-grace period in ms when pointer leaves trigger. Default 150. */
  closeGraceMs?: number;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Returns Tailwind intensity class based on 0..1 intensity value.
 * Matches spec §3.7 color table.
 */
function intensityClass(intensity: number): string {
  if (intensity <= 0) return 'bg-muted/30';
  if (intensity <= 0.25) return 'bg-[hsl(var(--chart-1)/0.30)]';
  if (intensity <= 0.5) return 'bg-[hsl(var(--chart-1)/0.55)]';
  if (intensity <= 0.75) return 'bg-[hsl(var(--chart-1)/0.75)]';
  return 'bg-[hsl(var(--chart-1))]';
}

/**
 * Picks a readable text color for the day-of-month digit inside a cell.
 * Equivalent to the per-intensity contrast helper — inlined because the helper
 * is not exported. Forbidden: writing a third color palette.
 *
 * Light theme: dark text on low/zero intensity cells, white on saturated.
 * Dark  theme: light foreground on muted/30, white on saturated blue.
 */
function pickReadableForeground(intensity: number, theme: 'light' | 'dark'): string {
  if (intensity <= 0.5) {
    return theme === 'dark' ? 'text-foreground' : 'text-slate-900';
  }
  return 'text-white';
}

function formatTransactionCount(count: number): string {
  return `${count} ${count === 1 ? 'transaction' : 'transactions'}`;
}

/**
 * ISO weekday index where 0 = Mon … 6 = Sun.
 */
function getIsoWeekday(dateStr: string): number {
  const d = new Date(dateStr);
  return (d.getDay() + 6) % 7;
}

/**
 * Returns 0-based ISO week index relative to the Monday of the first day's week.
 */
function getIsoWeekIndex(dateStr: string, firstDate: string): number {
  const start = new Date(firstDate);
  const current = new Date(dateStr);
  const startMon = new Date(start);
  startMon.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  const diffMs = current.getTime() - startMon.getTime();
  return Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
}

/**
 * Counts distinct (year, month) tuples present in `days`. Used by the vertical
 * projection to decide whether the heatmap fits without vertical scrolling
 * (≤ 2 months) or must be wrapped in a `max-h-[520px] overflow-y-auto` container.
 *
 * Examples:
 *   • 2026-06-01 → 2026-07-15  → 2 months
 *   • 2026-06-01 → 2026-08-22  → 3 months
 *   • 2026-06-01 → 2026-12-31  → 7 months
 *   • 2026-12-25 → 2027-01-05  → 2 months (Dec + Jan) — count spans year boundaries
 *
 * Returns 1 for an empty array as the "no-scroll default".
 */
export function visibleMonthCount(days: CalendarMatrixDay[]): number {
  if (!days || days.length === 0) return 1;
  const keys = new Set<string>();
  for (const d of days) {
    if (!d || typeof d.date !== 'string') continue;
    const dt = new Date(d.date + 'T00:00:00');
    keys.add(`${dt.getFullYear()}-${dt.getMonth()}`);
  }
  return keys.size;
}

/**
 * Returns true when `day` is the first day of a new month AND that month is
 * not the very first month in the supplied sorted-date range. Used by the
 * vertical projection to render the `border-l-2 border-l-ring` month-boundary
 * marker.
 */
function isFirstDayOfNewMonth(day: CalendarMatrixDay | undefined | null, sortedDates: string[]): boolean {
  if (!day || typeof day.date !== 'string' || day.date.length < 10 || day.date.slice(8, 10) !== '01') return false;
  const idx = sortedDates.indexOf(day.date);
  if (idx <= 0) return false;
  const prev = sortedDates[idx - 1];
  return prev.slice(0, 7) !== day.date.slice(0, 7);
}

/** Width of the left-edge vertical month-name gutter (px). Sticky-friendly. */
const MONTH_GUTTER_WIDTH_PX = 40;

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * One continuous gutter card per (year, month) on the vertical projection. Spans
 * the consecutive ISO-week rows that belong to the same calendar month so the
 * user sees a single "Jun / Jul / Aug" card instead of one repeated abbreviation
 * per week row. Empty weeks (no occupied days) are dropped — they neither split
 * a span nor render a gutter card. The card height equals `rowSpan * (cellSize
 * + gap) - gap` so adjacent cards touch with exactly one `gap` between them.
 */
interface GutterMonthCard {
  /** "YYYY-MM" — React key and `data-month-key` hook. */
  monthKey: string;
  /** Short abbreviation like "Jun" — rendered inside the card. */
  label: string;
  /** 0-based ISO week index of the first row in this span (inclusive). */
  weekIndex: number;
  /** Number of consecutive ISO week rows in this span. */
  rowSpan: number;
  /** Pixel height of the card: `rowSpan * (cellSize + gap) - gap`. */
  heightPx: number;
}

/**
 * Fold consecutive ISO-weeks of the same calendar (year, month) into a single
 * card. Returns one entry per distinct (year, month) that has at least one
 * occupied week. When a week crosses a month boundary, its later month wins so
 * the gutter card starts on the first row containing that month's dates. Year
 * boundary correctness: 2025-12 and 2026-01 do NOT merge because their
 * (year, month) tuples differ.
 */
function buildGutterMonthCards(
  days: CalendarMatrixDay[],
  cellSize: number,
  gap: number,
): GutterMonthCard[] {
  if (!days || days.length === 0) return [];
  const sortedDays = [...days]
    .filter((d) => d && typeof d.date === 'string')
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  if (sortedDays.length === 0) return [];
  const firstDate = sortedDays[0].date;
  const weekCount = sortedDays.reduce(
    (max, d) => Math.max(max, getIsoWeekIndex(d.date, firstDate) + 1),
    0,
  );
  // Assign each week to its latest occupied month so a month label starts on
  // the first row containing that month's dates, including boundary weeks.
  const weekKey: (string | null)[] = new Array(weekCount).fill(null);
  const weekLabel: (string | null)[] = new Array(weekCount).fill(null);
  for (const d of sortedDays) {
    const w = getIsoWeekIndex(d.date, firstDate);
    if (w < 0 || w >= weekCount) continue;
    const dt = new Date(d.date + 'T00:00:00');
    const m = dt.getMonth();
    weekKey[w] = `${dt.getFullYear()}-${String(m + 1).padStart(2, '0')}`;
    weekLabel[w] = MONTH_ABBR[m];
  }
  // Fold consecutive weeks of the same (year, month) tuple into a single card.
  const cards: GutterMonthCard[] = [];
  let i = 0;
  while (i < weekCount) {
    const key = weekKey[i];
    const label = weekLabel[i];
    if (key === null || label === null) { i += 1; continue; }
    let j = i;
    while (j + 1 < weekCount && weekKey[j + 1] === key) j += 1;
    const rowSpan = j - i + 1;
    cards.push({
      monthKey: key,
      label,
      weekIndex: i,
      rowSpan,
      heightPx: rowSpan * (cellSize + gap) - gap,
    });
    i = j + 1;
  }
  return cards;
}

/**
 * Compute the monthly labels for the horizontal-projection month strip.
 * Equivalent to the original buildMonthLabels helper. Returns one entry per
 * ISO week column; the first represented week always shows the first
 * supplied day's month; later weeks may show another month when a newer
 * first-of-month appears in that column.
 */
function buildMonthStripLabels(
  days: CalendarMatrixDay[],
  firstDate: string,
  weekCount: number,
): string[] {
  const labels: string[] = new Array(weekCount).fill('');
  if (weekCount === 0 || days.length === 0) return labels;

  const sortedDays = [...days].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
  );

  let prevMonth: number | null = null;
  for (const d of sortedDays) {
    const weekIdx = getIsoWeekIndex(d.date, firstDate);
    if (weekIdx < 0 || weekIdx >= weekCount) continue;
    const month = new Date(d.date + 'T00:00:00').getMonth();
    if (prevMonth === null || month !== prevMonth) {
      labels[weekIdx] = new Date(d.date + 'T00:00:00').toLocaleDateString(
        undefined,
        { month: 'short' },
      );
      prevMonth = month;
    }
  }
  return labels;
}

export function CalendarMatrix({
  days,
  cellSize,
  gap = 2,
  showDayLabels = true,
  orientation = 'horizontal',
  activeDate,
  onActiveDateChange,
  onViewTransactions,
  closeGraceMs = 150,
}: CalendarMatrixProps) {
  const isHorizontal = orientation === 'horizontal';
  const effectiveCellSize = cellSize ?? (isHorizontal ? 24 : 44);
  const effectiveTheme =
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')
      ? 'dark'
      : 'light';
  const { fmt } = useCurrency();
  const [floatingBarTopPadding, setFloatingBarTopPadding] = useState(8);

  useEffect(() => {
    const updateFloatingBarBoundary = () => {
      const bar = document.querySelector('[data-testid="unified-topbar"]')?.getBoundingClientRect();
      const chevron = document.querySelector('[data-testid="floating-bar-collapse-toggle"]')?.getBoundingClientRect();
      setFloatingBarTopPadding(Math.max(8, (bar?.bottom ?? 0) + 8, (chevron?.bottom ?? 0) + 8));
    };
    updateFloatingBarBoundary();
    window.addEventListener('resize', updateFloatingBarBoundary);
    window.addEventListener('scroll', updateFloatingBarBoundary, true);
    return () => {
      window.removeEventListener('resize', updateFloatingBarBoundary);
      window.removeEventListener('scroll', updateFloatingBarBoundary, true);
    };
  }, [activeDate]);

  const safeDays = days.filter((d) => d && typeof d.date === "string");
  const firstDate = safeDays[0]?.date ?? days[0]?.date ?? "";
  const weekCount = safeDays.reduce(
    (max, d) => Math.max(max, getIsoWeekIndex(d.date, firstDate) + 1),
    0,
  );
  const labelWidth = showDayLabels ? 28 : 0;

  const monthStripLabels = buildMonthStripLabels(safeDays, firstDate, weekCount);

  // Vertical: 7 weekday columns (1..7); the gutter is a sibling `<aside>`, not a
  // grid column. Horizontal: label col + N week cols (unchanged).
  // Fixed `44px` per cell on mobile so 7×44 + 6×1 gaps + 40 gutter + 1 inner-row gap
  // = 355 px ≤ 358 px viewport shell budget at 390×844.
  const dayGridTemplateColumns = isHorizontal
    ? `${labelWidth}px repeat(${weekCount}, ${effectiveCellSize}px)`
    : `repeat(7, minmax(0, 1fr))`;
  const dayGridTemplateRows = isHorizontal
    ? `repeat(7, ${effectiveCellSize}px)`
    : `repeat(${weekCount}, ${effectiveCellSize}px)`;
  // Vertical: shrink the grid gap from 2 → 1 px so the 7×44 grid + 6×1 gaps + 40
  // gutter fits inside the 358 px shell budget with no horizontal scroll.
  const dayGridGap = !isHorizontal ? 1 : 2;


  const monthGutterCards = useMemo(
    () => (isHorizontal ? [] : buildGutterMonthCards(days, effectiveCellSize, dayGridGap)),
    [isHorizontal, days, effectiveCellSize, dayGridGap],
  );

  const cellColumn = (day: CalendarMatrixDay): number =>
    isHorizontal
      ? getIsoWeekIndex(day.date, firstDate) + (showDayLabels ? 2 : 1)
      : getIsoWeekday(day.date) + 1; // weekday columns start at 1 (gutter is sibling, not a grid column)
  const cellRow = (day: CalendarMatrixDay): number =>
    isHorizontal
      ? getIsoWeekday(day.date) + 1
      : getIsoWeekIndex(day.date, firstDate) + 1;

  // Vertical: 7 weekday columns (no gutter col); horizontal: label col + N week cols.
  const headerGridTemplateColumns = isHorizontal
    ? `${labelWidth}px repeat(${weekCount}, ${effectiveCellSize}px)`
    : `repeat(7, ${effectiveCellSize}px)`;
  // Vertical: weekday-header strip starts at col 1 (gutter is sibling, not in grid); horizontal unchanged.
  const firstDataColumn = isHorizontal ? (showDayLabels ? 2 : 1) : 1;

  // --- Active-cell controlled popover state ---
  const closeTimerRef = useRef<number | null>(null);
  const isPointerInPopupRef = useRef(false);
  const lastPointerTypeRef = useRef<'mouse' | 'touch' | 'pen' | 'unknown'>('mouse');
  // Tracks the most recent focus event so the subsequent click (which always
  // fires after focus on both mouse and touch devices) can detect and skip
  // the toggle, preserving the focus-driven popover open.
  const lastFocusRef = useRef<{ key: string; ts: number } | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      if (!isPointerInPopupRef.current) {
        onActiveDateChange(null);
      }
    }, closeGraceMs);
  }, [clearCloseTimer, onActiveDateChange, closeGraceMs]);

  // Cancel pending close on unmount.
  useEffect(
    () => () => {
      clearCloseTimer();
    },
    [clearCloseTimer],
  );

  // --- Cell handlers — split by pointer path. ---
  const handleCellPointerEnter = useCallback(
    (key: string, e: React.PointerEvent<HTMLButtonElement>) => {
      const pt = e.pointerType || '';
      if (pt === 'touch') return;
      lastPointerTypeRef.current = pt === 'pen' ? 'pen' : 'mouse';
      isPointerInPopupRef.current = false;
      clearCloseTimer();
      if (activeDate !== key) {
        onActiveDateChange(key);
      }
    },
    [activeDate, onActiveDateChange, clearCloseTimer],
  );

  const handleCellPointerLeave = useCallback(
    (key: string, e: React.PointerEvent<HTMLButtonElement>) => {
      const pt = e.pointerType || '';
      if (pt === 'touch') return;
      if (activeDate === key) {
        scheduleClose();
      }
    },
    [activeDate, scheduleClose],
  );
  const handleCellPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      lastPointerTypeRef.current = (e.pointerType || 'mouse') as 'mouse' | 'touch' | 'pen' | 'unknown';
    },
    [],
  );

  const handleCellClick = useCallback(
    (key: string) => {
      // If focus just opened the popover for this key in the last 50ms,
      // do not toggle — the focus-driven open should remain.
      const recent = lastFocusRef.current;
      if (recent && recent.key === key && Date.now() - recent.ts < 50) {
        return;
      }
      if (activeDate === key) {
        onActiveDateChange(null);
      } else {
        onActiveDateChange(key);
      }
    },
    [activeDate, onActiveDateChange],
  );

  const handleCellKeyDown = useCallback(
    (key: string, e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        // If focus just opened the popover for this key in the last 50ms,
        // do not toggle — the focus-driven open should remain.
        const recent = lastFocusRef.current;
        if (recent && recent.key === key && Date.now() - recent.ts < 50) {
          return;
        }
        if (activeDate === key) {
          onActiveDateChange(null);
        } else {
          onActiveDateChange(key);
        }
      } else if (e.key === 'Escape' && activeDate != null) {
        e.preventDefault();
        onActiveDateChange(null);
      }
    },
    [activeDate, onActiveDateChange],
  );

  const handleCellFocus = useCallback(
    (key: string) => {
      if (lastPointerTypeRef.current === 'touch') return; // touch already handles via onClick
      isPointerInPopupRef.current = false;
      clearCloseTimer();
      lastFocusRef.current = { key, ts: Date.now() };
      if (activeDate !== key) {
        onActiveDateChange(key);
      }
    },
    [activeDate, onActiveDateChange, clearCloseTimer],
  );

  const focusCell = useCallback((key: string) => {
    requestAnimationFrame(() => {
      const cell = document.querySelector<HTMLButtonElement>(`[data-cell-iso="${key}"]`);
      cell?.focus();
    });
  }, []);

  const handleViewTransactions = useCallback(
    (key: string) => {
      onViewTransactions?.(key, () => focusCell(key));
      onActiveDateChange(null);
    },
    [focusCell, onActiveDateChange, onViewTransactions],
  );


  // --- Vertical projection month-count scroll cap (M3 follow-up). ---
  const sortedDates = useMemo(() => [...days].map((d) => d.date).sort(), [days]);
  if (days.length === 0) return null;
  const monthCap = visibleMonthCount(days);
  const twoMonthsVisible = isHorizontal || monthCap <= 2;
  const applyVerticalScrollWrapper = !twoMonthsVisible;

  // Day-number font/weight policy: 14px semibold on mobile, 10px medium on desktop.
  const cellNumberFontSize = effectiveCellSize >= 44 ? 'text-[14px]' : 'text-[10px]';
  const cellNumberFontWeightBase = effectiveCellSize >= 44 ? 'font-semibold' : 'font-medium';

// Day cells — one Radix Popover per cell, controlled by activeDate.
  // Extracted as a const so we can render it inside either the horizontal grid
  // (which owns weekday labels in column 1) or the vertical grid (which is
  // sibling of the gutter aside). The closure captures all per-cell helpers.
  const dayCells = days
    .filter((day) => day && typeof day.date === 'string')
    .map((day) => {
      const col = cellColumn(day);
      const row = cellRow(day);
      const a11yLabel = `${day.date}: ${fmt(day.total)}, ${formatTransactionCount(day.count)}`;
      const isActive = activeDate === day.date;
      const dayNumber = day.date.length >= 10 ? day.date.slice(8, 10) : '';
      const isMonthBoundary =
        !isHorizontal && isFirstDayOfNewMonth(day, sortedDates);
      const cellNumberColor = pickReadableForeground(day.intensity, effectiveTheme);
      const cellNumberWeight =
        !isHorizontal && day.intensity <= 0.5
          ? 'font-semibold'
          : cellNumberFontWeightBase;

      return (
        <Popover
          key={day.date}
          open={isActive}
          onOpenChange={(open) => {
            if (!open && activeDate === day.date) {
              onActiveDateChange(null);
            }
          }}
        >
          <PopoverTrigger asChild>
            <button
              role="gridcell"
              aria-label={a11yLabel}
              aria-expanded={isActive}
              aria-haspopup="dialog"
              tabIndex={0}
              data-cell-iso={day.date}
              data-weekday={getIsoWeekday(day.date)}
              data-week-index={getIsoWeekIndex(day.date, firstDate)}
              data-active={isActive ? 'true' : 'false'}
              data-month-boundary={isMonthBoundary ? 'true' : 'false'}
              className={cn(
                'relative rounded-sm focus-visible:ring-2 focus-visible:ring-ring outline-none transition-opacity flex items-center justify-center',
                intensityClass(day.intensity),
                isActive && 'ring-2 ring-ring ring-offset-1 ring-offset-background',
                isMonthBoundary && 'border-l-2 border-l-ring',
              )}
              style={{
                gridColumn: col,
                gridRow: row,
                width: isHorizontal ? effectiveCellSize : '100%',
                height: effectiveCellSize,
                minWidth: isHorizontal ? effectiveCellSize : 0,
                minHeight: isHorizontal ? effectiveCellSize : 44,
              }}
              onPointerEnter={(e) => handleCellPointerEnter(day.date, e)}
              onPointerLeave={(e) => handleCellPointerLeave(day.date, e)}
              onPointerDown={handleCellPointerDown}
              onClick={() => handleCellClick(day.date)}
              onKeyDown={(e) => handleCellKeyDown(day.date, e)}
              onFocus={() => handleCellFocus(day.date)}
            >
              {/* Day-of-month digit — purely visual; the aria-label above
                  remains the semantic source-of-truth. */}
              {!isHorizontal && (
                <span
                  aria-hidden="true"
                  data-day-number=""
                  className={cn(
                    'pointer-events-none select-none leading-none',
                    'flex items-center justify-center w-full h-full',
                    cellNumberFontSize,
                    cellNumberWeight,
                    cellNumberColor,
                  )}
                >
                  {dayNumber}
                </span>
              )}
              {isHorizontal && (
                <span
                  aria-hidden="true"
                  data-day-number=""
                  className={cn(
                    'pointer-events-none select-none leading-none',
                    'flex items-center justify-center w-full h-full',
                    cellNumberFontSize,
                    cellNumberWeight,
                    cellNumberColor,
                  )}
                >
                  {dayNumber}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="center"
            sideOffset={6}
            collisionPadding={{ top: floatingBarTopPadding, right: 8, bottom: 8, left: 8 }}
            avoidCollisions
            sticky="partial"
            className="w-fit min-w-0 max-w-[12rem] p-2 text-[10px] leading-tight"
            data-testid="calendar-cell-popover"
            data-cell-popover-iso={day.date}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onMouseEnter={() => {
              isPointerInPopupRef.current = true;
              clearCloseTimer();
            }}
            onMouseLeave={() => {
              isPointerInPopupRef.current = false;
              scheduleClose();
            }}
          >
            <div className="space-y-0.5">
              <p className="font-medium">{day.date}</p>
              <p>{fmt(day.total)}</p>
              <p className="text-muted-foreground">
                {formatTransactionCount(day.count)}
              </p>
              {onViewTransactions && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-1 min-h-[44px] min-w-[44px] w-full px-2 text-[10px]"
                  data-testid="calendar-view-transactions"
                  data-cell-iso={day.date}
                  onClick={() => handleViewTransactions(day.date)}
                >
                  View transactions
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      );
    });
  return (
    <div
      data-testid="calendar-month-scroll-scope"
      data-orientation={orientation}
      data-month-count={monthCap}
      className={cn(
        'flex flex-col gap-1',
        isHorizontal ? 'inline-flex w-max' : 'min-w-full items-start',
      )}
    >
{/* Horizontal-projection month strip header.
Vertical projection instead puts weekday labels at the top. */}
{isHorizontal && (
        <div
          data-testid="calendar-month-headers"
          aria-hidden="true"
          className="grid"
          style={{
            gridTemplateColumns: headerGridTemplateColumns,
            gridTemplateRows: '16px',
            gap: `${gap}px`,
          }}
        >
          {monthStripLabels.map((label, i) => (
            <div
              key={`mh-${i}`}
              data-week-index={i}
              className="truncate text-[10px] font-medium leading-none text-muted-foreground"
              style={{
                gridColumn: firstDataColumn + i,
                gridRow: 1,
                width: effectiveCellSize,
              }}
            >
              {label}
            </div>
          ))}
        </div>
      )}

      {/* Vertical scroll wrapper — applied only in vertical projection when monthCount > 2.
          The wrapper element is always mounted so we can read data-* attributes; the classNames
          are conditional via Tailwind's `cn()`. */}
      <div
        data-testid="calendar-vertical-scroll"
        data-month-count={monthCap}
        data-scroll-applied={applyVerticalScrollWrapper ? 'true' : 'false'}
        className={cn(
          'flex min-w-0 flex-col gap-1',
          isHorizontal ? 'w-full' : 'min-w-full',
          isHorizontal ? 'overflow-x-auto overflow-y-visible' : 'overflow-x-hidden',
          applyVerticalScrollWrapper && 'max-h-[520px] overflow-y-auto',
        )}
      >
        {isHorizontal ? (
          /* Horizontal projection: grid owns weekday labels in column 1 + day cells. */
          <div
            role="grid"
            aria-label="Spending calendar heatmap"
            className="grid"
            style={{
              gridTemplateColumns: dayGridTemplateColumns,
              gridTemplateRows: dayGridTemplateRows,
              gap: `${gap}px`,
            }}
          >
            {/* Horizontal-projection weekday-label column (sticky). Vertical puts
                weekday labels in a separate header strip above (rendered above). */}
            {showDayLabels &&
              DAY_LABELS.map((label, row) => (
                <div
                  key={label}
                  data-day-label={label}
                  aria-hidden="true"
                  className="sticky left-0 z-10 bg-background flex items-center justify-end pr-1 text-[10px] text-muted-foreground"
                  style={{
                    gridColumn: 1,
                    gridRow: row + 1,
                    width: labelWidth,
                  }}
                >
                  {label}
                </div>
              ))}
            {dayCells}
          </div>
        ) : (
          /* Vertical projection: flex column with header row + day row.
             Each row has the same structure: 40px spacer/gutter + 1px gap + 7-col grid.
             The header row's 7-col grid aligns column-for-column with the day row's
             7-col grid. `columnGap: 0; rowGap: 1px;` eliminates 6×1px = 6px of horizontal
             overhead. `width: '100%'` on the grid makes it fit the available inner-row space. */
          <div className="flex flex-col min-w-0" style={{ gap: '1px' }}>
            {/* Header row — spacer (40px) + 1px gap + 7-col grid matching the day grid */}
            {showDayLabels && (
              <div
                data-testid="calendar-weekday-headers"
                aria-hidden="true"
                className="flex flex-row items-start min-w-0 sticky top-0 z-20 bg-background"
                style={{ gap: '1px' }}
              >
                <aside className="shrink-0" style={{ width: MONTH_GUTTER_WIDTH_PX }} aria-hidden="true" />
                <div
                  className="grid grow"
                  style={{
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    columnGap: 0,
                    rowGap: 1,
                    width: '100%',
                  }}
                >
                  {DAY_LABELS.map((label) => (
                    <div
                      key={label}
                      data-day-label={label}
                      aria-hidden="true"
                      className={cn(
                        'flex items-center justify-center',
                        'rounded-md bg-muted/40 ring-1 ring-border/60',
                        'text-[10px] font-medium leading-none text-muted-foreground',
                        'select-none',
                      )}
                      style={{ height: effectiveCellSize }}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Day row — gutter (40px) + 1px gap + 7-col grid */}
            <div className="flex flex-row items-start min-w-0" style={{ gap: '1px' }}>
              <aside
                data-testid="calendar-month-gutter"
                aria-hidden="true"
                className="flex flex-col shrink-0"
                style={{ width: MONTH_GUTTER_WIDTH_PX, gap: `${dayGridGap}px` }}
              >
                {monthGutterCards.map((card) => (
                  <div
                    key={card.monthKey}
                    data-month-gutter=""
                    data-month-key={card.monthKey}
                    data-row-span={card.rowSpan}
                    className={cn(
                      'flex items-center justify-center',
                      'rounded-md bg-muted/40 ring-1 ring-border/60',
                      'text-[10px] font-semibold leading-none text-foreground',
                      'select-none',
                    )}
                    style={{
                      height: card.heightPx,
                      width: '100%',
                    }}
                  >
                    <span
                      data-month-gutter-label=""
                      className="leading-none"
                      style={{ transform: 'rotate(-90deg)' }}
                    >
                      {card.label}
                    </span>
                  </div>
                ))}
              </aside>
              <div
                role="grid"
                aria-label="Spending calendar heatmap"
                className="grid grow"
                style={{
                  gridTemplateColumns: dayGridTemplateColumns,
                  gridTemplateRows: dayGridTemplateRows,
                  columnGap: 0,
                  rowGap: 1,
                  width: '100%',
                }}
              >
                {dayCells}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
